import { useMemo } from 'react';
import type { ConciergeMessagePart } from '../types/conciergeToolTypes';

export interface LiveTakeoverState {
  /** True when a human teammate is currently in charge of the thread */
  active: boolean;
  intercomConversationId?: string;
  teamId?: string;
  adminId?: string;
  adminName?: string;
  adminAvatarUrl?: string;
  /** ISO timestamp of the most recent takeover-started event we observed */
  startedAt?: string;
}

/**
 * Derives takeover state from the parts stream of all messages on the chat.
 *
 * The state machine is `data-live-takeover-active` (initial state — fired
 * by the streaming entry point when a turn begins in human mode) or
 * `data-live-takeover-started` (transition — fired when request_live_agent
 * runs) → optional `data-takeover-team-changed` → `data-live-takeover-ended`.
 *
 * We walk parts in order so the most recent state wins.
 */
export function useLiveTakeoverState(parts: ConciergeMessagePart[]): LiveTakeoverState {
  return useMemo(() => {
    let active = false;
    let intercomConversationId: string | undefined;
    let teamId: string | undefined;
    let adminId: string | undefined;
    let adminName: string | undefined;
    let adminAvatarUrl: string | undefined;
    let startedAt: string | undefined;
    // Once a takeover has explicitly ended (user clicked "Return to Concierge"
    // or the admin closed the conversation), subsequent stray events like a
    // late `data-human-reply` must NOT re-activate the banner — only a fresh
    // `data-live-takeover-started` (or `data-live-takeover-active` from a
    // streaming entry point) re-arms it.
    let explicitlyEnded = false;
    // Has the backend told us its canonical state? `data-live-takeover-state`
    // is the authoritative source of truth (sent by the backend on events
    // SSE connect, reflecting DynamoDB). When present, it WINS over any
    // history-derived state — events stay merely advisory.
    let canonicalActive: boolean | undefined;

    for (const part of parts) {
      if (part.type === 'data-live-takeover-state') {
        const data = (part as any).data;
        canonicalActive = !!data?.active;
        if (data?.intercomConversationId) intercomConversationId = data.intercomConversationId;
        if (data?.teamId) teamId = data.teamId;
        if (data?.adminId) adminId = data.adminId;
        if (data?.adminName) adminName = data.adminName;
        if (data?.adminAvatarUrl) adminAvatarUrl = data.adminAvatarUrl;
        if (data?.startedAt) startedAt = data.startedAt;
        // The canonical state is also the truth for explicitlyEnded:
        // backend says inactive => any derived "active" was wrong.
        if (!data?.active) explicitlyEnded = true;
      } else if (part.type === 'data-live-takeover-active') {
        const data = (part as any).data;
        active = true;
        canonicalActive = true;
        explicitlyEnded = false;
        intercomConversationId = data?.intercomConversationId ?? intercomConversationId;
      } else if (part.type === 'data-live-takeover-started') {
        const data = (part as any).data;
        active = true;
        canonicalActive = true;
        explicitlyEnded = false;
        intercomConversationId = data?.intercomConversationId ?? intercomConversationId;
        teamId = data?.teamId ?? teamId;
        startedAt = startedAt ?? new Date().toISOString();
      } else if (part.type === 'data-takeover-team-changed') {
        const data = (part as any).data;
        teamId = data?.teamId ?? teamId;
        adminId = data?.adminId ?? adminId;
      } else if (part.type === 'data-human-reply') {
        // Always record the latest admin metadata so a synthetic chat bubble
        // can use it. But only treat this as an "active takeover" signal when
        // the takeover hasn't been explicitly ended.
        const data = (part as any).data;
        adminName = data?.adminName ?? adminName;
        adminAvatarUrl = data?.adminAvatarUrl ?? adminAvatarUrl;
        if (!explicitlyEnded) active = true;
      } else if (part.type === 'data-live-takeover-ended') {
        active = false;
        canonicalActive = false;
        explicitlyEnded = true;
      }
    }

    return {
      // Backend canonical state wins when known; otherwise fall back to fold.
      active: canonicalActive !== undefined ? canonicalActive : active,
      intercomConversationId,
      teamId,
      adminId,
      adminName,
      adminAvatarUrl,
      startedAt,
    };
  }, [parts]);
}
